import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AutoCompleteOption, useDebounce } from '../../../../../components-dummy';
import { campaignModalPageActions } from '../../Actions';
import { SuggestedOffer } from '../../../../../services/src/service/email-marketing-campaigns/types';

interface UseAutoCompleteFetchStateReturn {
  selectedValue: string;
  onChange: (value: string) => void;
  options: AutoCompleteOption[];
  onSelectOption: (selectedOption: any) => void;
  imageUrl: string;
}

interface UseAutoCompleteFetchState {
  shopId: number;
  selectedOffer: SuggestedOffer;
}

export const useAutoCompleteFetchState = ({
  shopId,
  selectedOffer,
}: UseAutoCompleteFetchState): UseAutoCompleteFetchStateReturn => {
  const appDispatch = useDispatch();

  const { sku = '', title, imageUrl } = selectedOffer || {};

  const [autocompleteOptions, setAutocompleteOptions] = useState([]);

  const fetchResultsFromApi = useCallback(
    async (searchTerm: string, locale?: string) => {
      const { offers } = await (
        appDispatch(
          campaignModalPageActions.getSearchResults({ shopId, searchTerm, locale })
        ) as any
      ).unwrap();
      setAutocompleteOptions(
        offers.map((data: SuggestedOffer) => {
          return {
            value: data.sku,
            title: data.sku,
            data,
          };
        })
      );
    },
    [shopId]
  );

  const fetchFunctionDebounced = useDebounce(fetchResultsFromApi, 300);

  const onChange = useCallback(
    (searchValue: string): void => {
      fetchFunctionDebounced(searchValue);
    },
    [fetchFunctionDebounced]
  );

  useEffect(() => {
    fetchResultsFromApi(sku || '');
  }, [fetchResultsFromApi]);

  const selectedValue = useMemo(() => (title ? `${title}, ${sku}` : `${sku}`), [selectedOffer]);

  return {
    selectedValue,
    onChange,
    options: autocompleteOptions,
    onSelectOption: onChange,
    imageUrl,
  };
};
